import { useState, useEffect, useRef } from 'react';
import { collection, addDoc } from 'firebase/firestore';
import { Geolocation } from '@capacitor/geolocation';
import { Capacitor } from '@capacitor/core';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import DriveDetector from '../plugins/DriveDetector';

const IRS_RATE = 0.70;
const isNative = Capacitor.isNativePlatform();

function haversineDistance(lat1, lng1, lat2, lng2) {
  const R = 3958.8; // Earth radius in miles
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function formatDuration(seconds) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

export default function NewTripGPS() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [tracking, setTracking] = useState(false);
  const [paused, setPaused] = useState(false);
  const [distance, setDistance] = useState(0);
  const [duration, setDuration] = useState(0);
  const [speed, setSpeed] = useState(0);
  const [coordinates, setCoordinates] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [purpose, setPurpose] = useState('Business');

  const watchIdRef = useRef(null);
  const startTimeRef = useRef(null);
  const timerRef = useRef(null);
  const lastCoordRef = useRef(null);
  const coordsRef = useRef([]);

  async function startTracking() {
    setError('');

    // On native, use the native DriveDetector plugin and navigate to dashboard
    if (isNative) {
      try {
        await DriveDetector.startManualTrip();
        navigate('/dashboard');
        return;
      } catch (err) {
        console.error('Failed to start native trip:', err);
        setError('Failed to start trip. Check location permissions.');
        return;
      }
    }

    // Web fallback — use Geolocation API directly
    startTimeRef.current = new Date();
    setTracking(true);
    setPaused(false);
    setDistance(0);
    setDuration(0);
    setCoordinates([]);
    coordsRef.current = [];
    lastCoordRef.current = null;

    timerRef.current = setInterval(() => {
      if (startTimeRef.current) {
        setDuration(Math.floor((Date.now() - startTimeRef.current.getTime()) / 1000));
      }
    }, 1000);

    Geolocation.watchPosition(
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
      (position, err) => {
        if (err) {
          console.error('GPS error:', err);
          return;
        }
        if (!position) return;

        const coord = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          timestamp: position.timestamp,
          speed: Math.max(0, position.coords.speed || 0),
        };

        if (lastCoordRef.current) {
          const d = haversineDistance(
            lastCoordRef.current.lat, lastCoordRef.current.lng,
            coord.lat, coord.lng
          );
          if (d > 0.006) {
            setDistance((prev) => prev + d);
            lastCoordRef.current = coord;
          }
        } else {
          lastCoordRef.current = coord;
        }

        coordsRef.current.push(coord);
        setCoordinates([...coordsRef.current]);
        setSpeed(Math.round((coord.speed || 0) * 2.237));
      }
    ).then((id) => {
      watchIdRef.current = id;
    });
  }

  async function stopTracking() {
    if (watchIdRef.current !== null) {
      Geolocation.clearWatch({ id: watchIdRef.current });
      watchIdRef.current = null;
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setTracking(false);
    setPaused(false);

    // Auto-save if trip has enough data
    if (coordsRef.current.length >= 2 && distance >= 0.1) {
      setSaving(true);
      try {
        const first = coordsRef.current[0];
        const last = coordsRef.current[coordsRef.current.length - 1];
        await addDoc(collection(db, 'users', currentUser.uid, 'trips'), {
          date: startTimeRef.current.toISOString(),
          endTime: new Date().toISOString(),
          startLat: first.lat,
          startLng: first.lng,
          endLat: last.lat,
          endLng: last.lng,
          startAddress: '',
          endAddress: '',
          distance: parseFloat(distance.toFixed(2)),
          durationMinutes: parseFloat((duration / 60).toFixed(1)),
          deduction: parseFloat((distance * IRS_RATE).toFixed(2)),
          irsRate: IRS_RATE,
          coordinates: coordsRef.current,
          trackingMethod: 'manual-gps',
          purpose,
          createdAt: new Date().toISOString(),
        });
        navigate('/dashboard');
        return;
      } catch (err) {
        setError('Failed to save trip. You can retry below.');
        console.error('Auto-save error:', err);
      }
      setSaving(false);
    }
  }

  async function saveTrip() {
    if (coordsRef.current.length < 2 || distance < 0.1) {
      setError('Trip too short to save.');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const first = coordsRef.current[0];
      const last = coordsRef.current[coordsRef.current.length - 1];

      await addDoc(collection(db, 'users', currentUser.uid, 'trips'), {
        date: startTimeRef.current.toISOString(),
        endTime: new Date().toISOString(),
        startLat: first.lat,
        startLng: first.lng,
        endLat: last.lat,
        endLng: last.lng,
        startAddress: '',
        endAddress: '',
        distance: parseFloat(distance.toFixed(2)),
        durationMinutes: parseFloat((duration / 60).toFixed(1)),
        deduction: parseFloat((distance * IRS_RATE).toFixed(2)),
        irsRate: IRS_RATE,
        coordinates: coordsRef.current,
        trackingMethod: 'manual-gps',
        purpose,
        createdAt: new Date().toISOString(),
      });
      navigate('/dashboard');
    } catch (err) {
      setError('Failed to save trip. Please try again.');
      console.error('Save trip error:', err);
    }
    setSaving(false);
  }

  function discardTrip() {
    stopTracking();
    setDistance(0);
    setDuration(0);
    setCoordinates([]);
    coordsRef.current = [];
    startTimeRef.current = null;
  }

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (watchIdRef.current !== null) {
        Geolocation.clearWatch({ id: watchIdRef.current });
      }
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  // Pre-tracking state
  if (!tracking && coordinates.length === 0) {
    return (
      <div className="screen-container">
        <h2>GPS Trip</h2>
        <div className="gps-start-screen">
          <div className="gps-start-icon">
            <svg viewBox="0 0 100 100" width="100" height="100">
              <circle cx="50" cy="50" r="40" fill="none" stroke="#0D9488" strokeWidth="3" opacity="0.3" />
              <circle cx="50" cy="50" r="28" fill="none" stroke="#0D9488" strokeWidth="2" opacity="0.5" />
              <circle cx="50" cy="50" r="14" fill="#14B8A6" />
              <circle cx="50" cy="50" r="6" fill="#F0FDFA" />
            </svg>
          </div>
          <p className="gps-start-desc">
            Start a GPS-tracked trip. We'll record your route, distance, and calculate your deduction in real time.
          </p>

          <div className="gps-purpose-select">
            <label>Trip Purpose</label>
            <div className="purpose-toggle">
              <button
                className={`purpose-btn ${purpose === 'Business' ? 'active' : ''}`}
                onClick={() => setPurpose('Business')}
              >
                Business
              </button>
              <button
                className={`purpose-btn ${purpose === 'Personal' ? 'active' : ''}`}
                onClick={() => setPurpose('Personal')}
              >
                Personal
              </button>
            </div>
          </div>

          <button className="btn btn-primary gps-start-btn" onClick={startTracking}>
            Start Trip
          </button>
          <button className="btn btn-secondary" onClick={() => navigate('/dashboard')}>
            Cancel
          </button>
        </div>
      </div>
    );
  }

  // Active tracking / post-tracking state
  return (
    <div className="screen-container gps-tracking-screen">
      {/* Live stats */}
      <div className="gps-stats">
        <div className="gps-stat-main">
          <span className="gps-stat-value">{distance.toFixed(2)}</span>
          <span className="gps-stat-unit">miles</span>
        </div>
        <div className="gps-stat-row">
          <div className="gps-stat">
            <span className="gps-stat-label">Duration</span>
            <span className="gps-stat-val">{formatDuration(duration)}</span>
          </div>
          <div className="gps-stat">
            <span className="gps-stat-label">Speed</span>
            <span className="gps-stat-val">{tracking ? `${speed} mph` : '--'}</span>
          </div>
          <div className="gps-stat">
            <span className="gps-stat-label">Deduction</span>
            <span className="gps-stat-val" style={{ color: '#34D399' }}>${(distance * IRS_RATE).toFixed(2)}</span>
          </div>
        </div>
      </div>

      {/* Purpose toggle */}
      <div className="gps-purpose-inline">
        <button
          className={`purpose-chip ${purpose === 'Business' ? 'active' : ''}`}
          onClick={() => setPurpose('Business')}
        >
          Business
        </button>
        <button
          className={`purpose-chip ${purpose === 'Personal' ? 'active' : ''}`}
          onClick={() => setPurpose('Personal')}
        >
          Personal
        </button>
      </div>

      {/* Route info */}
      <div className="gps-route-info">
        <span>{coordinates.length} GPS points recorded</span>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Controls */}
      <div className="gps-controls">
        {tracking ? (
          <button className="gps-stop-btn" onClick={stopTracking}>
            <svg viewBox="0 0 24 24" width="32" height="32" fill="white">
              <rect x="6" y="6" width="12" height="12" rx="2" />
            </svg>
            <span>Stop Trip</span>
          </button>
        ) : (
          <>
            <button className="btn btn-primary" onClick={saveTrip} disabled={saving}>
              {saving ? 'Saving...' : 'Save Trip'}
            </button>
            <button className="btn btn-secondary" onClick={discardTrip}>
              Discard
            </button>
          </>
        )}
      </div>
    </div>
  );
}
